import {Component, EventEmitter, Input, OnInit, Output} from '@angular/core';
import {FormControl, FormGroup, Validators} from '@angular/forms';
import {MatDialog} from '@angular/material/dialog';
import {Router} from '@angular/router';
import {lastValueFrom} from 'rxjs';
import {User} from '../../../../core/model/user.model';
import {LoadingService} from "../../../../core/services/loading.service";
import {UserService} from '../../../../core/services/user.service';
import {ErrorPopupComponent} from '../../../../shared/components/dialogs/error-popup/error-popup.component';
import {DateUtilsService} from '../../../../shared/services/dateUtils.service';
import {
  AccountType,
  BankOption,
  PaymentMethodType,
  PaymentModelClient,
  PaymentModelDatabase
} from '../../model/payments.model';
import {BillingDetailsService} from '../../service/billing-details.service';
import {PaymentValidationService} from '../../service/payment-validation.service';
import {RetailerPaymentMethodsService} from '../../service/payments.service';

@Component({
  selector: 'app-banking-details',
  templateUrl: './banking-details.component.html',
  styleUrls: ['./banking-details.component.scss']
})
export class BankingDetailsComponent implements OnInit {
  @Input() editing: "billing" | "banking" | null = null;
  @Output() editingChange = new EventEmitter<"billing" | "banking" | null>();
  @Output() capitecWarning = new EventEmitter<boolean>();

  retailerPaymentMethodTypes: PaymentMethodType[] = [];
  paymentMethodsArray: PaymentModelDatabase[] = [];
  errMessage: string = '';
  bankOptions: BankOption[] = [];
  updateUsers: { id: number, name: string }[] = [];
  loading: boolean = false;
  valid: boolean = false;

  addPaymentMethodForm = new FormGroup({
    accountName: new FormControl('', [Validators.required]),
    bankAccountType: new FormControl('', [Validators.required]),
    bankName: new FormControl('', [Validators.required]),
    bankAccountNumber: new FormControl('', [Validators.required]),
    methodType: new FormControl({value: '', disabled: true}, [Validators.required]),
  });

  addingPaymentMethod: boolean = false;
  bankAccountTypes: AccountType[] = [];
  selectedPaymentMethod: string | null = null;
  paymentMethodsOpts =
    [
      {
        typeCode: "TRANSACT_PAYBACK",
        typeName: "Transact payback"
      }
    ]

  removingMethod: boolean = false;
  methodToRemove: PaymentModelDatabase | null = null;

  constructor(public paymentsService: RetailerPaymentMethodsService,
              private userService: UserService,
              private dialogService: MatDialog,
              private billingService: BillingDetailsService,
              private validateBankService: PaymentValidationService,
              public dateUtils: DateUtilsService,
              private router: Router,
              public loader: LoadingService) {
    this.bankAccountTypes = this.paymentsService.getBankAccountTypes();
  }

  ngOnInit(): void {
    this.loadPageData();
    this.resetPaymentMethodForm();

    this.addPaymentMethodForm.get('bankName')?.valueChanges
      .subscribe(value => {
        if (value != null && value == this.validateBankService.CAPITEC_BRANCH_CODE) {
          this.capitecWarning.emit(true);
        } else {
          this.capitecWarning.emit(false);
        }
      })
  }

  loadPageData(): void {
    this.loading = true;
    lastValueFrom(this.paymentsService.getAllOptions())
      .then((res: PaymentMethodType[]) => {
        this.retailerPaymentMethodTypes = res;
        if (this.paymentMethodsOpts.length == 1) {
          this.selectedPaymentMethod = this.paymentMethodsOpts[0].typeCode;
          this.addPaymentMethodForm.get('methodType')?.setValue(this.selectedPaymentMethod);
        }
        return this.validateBankService.getBankListWithDefaultBranchCode();
      }, err => {
        this.handleError(err);
        this.loading = false;
      })
      .then((bankList: BankOption[] | void) => {
        if (bankList) {
          this.bankOptions = bankList.sort((a, b) => a.bankName.localeCompare(b.bankName));
          this.resetPaymentMethodForm();
          return lastValueFrom(this.paymentsService.getAll());
        }
        return null;
      })
      .then((methods: PaymentModelDatabase[] | null) => {
        if (methods) {
          this.paymentMethodsArray = methods;
          this.getUpdateUsers();
        }
      })
      .finally(() => {
        this.loading = false;
      });
  }

  public resetPaymentMethodForm(): void {
    this.addPaymentMethodForm.reset();
    if (this.paymentMethodsOpts.length == 1) {
      this.selectedPaymentMethod = this.paymentMethodsOpts[0].typeCode;
      this.addPaymentMethodForm.get('methodType')?.setValue(this.selectedPaymentMethod);
    }
    this.capitecWarning.emit(false);
  }

  getUpdateUsers(): void {
    this.paymentMethodsArray.forEach(method => {
      lastValueFrom(this.userService.getUserById(method.updateUserId))
        .then((res: User) => {
          const user = {
            id: method.updateUserId,
            name: res.firstname + ' ' + res.lastname
          };
          this.updateUsers.push(user);
        }, err => {
          console.log(err);
        });
    });
  }

  getUsernameForId(userId: number): string {
    const user = this.updateUsers.filter(f => f.id === userId);
    if (user.length === 0) return '';
    return user[0].name;
  }

  removeMethod(outcome: boolean): void {
    if (outcome && this.methodToRemove != null) {
      this.loading = true;
      lastValueFrom(this.paymentsService.deletePaymentMethod(this.methodToRemove.uuid))
        .then(() => {
          this.loading = false;
          this.loadPageData();
          this.removingMethod = false;
          this.methodToRemove = null;
        }, (err: string) => {
          this.loading = false;
          this.handleError(err);
        });
    } else {
      this.removingMethod = false;
      this.methodToRemove = null;
    }
    this.capitecWarning.emit(false);
  }

  initiateRemoveMethod(method: PaymentModelDatabase): void {
    this.removingMethod = true;
    this.methodToRemove = method;
  }

  addMethod(): void {
    if (!this.loading) {
      const blankPaymentMethod: PaymentModelClient = {
        accountName: '', bankAccountNumber: '', bankAccountType: '', branchCode: '', methodType: ''
      };
      this.paymentsService.initPaymentMethod(blankPaymentMethod);
      // this.router.navigate(['account/settings/billing/add-banking-details']);
    }
  }

  updateMethod(method: PaymentModelDatabase): void {
    if (!this.loading) {
      const blankPaymentMethod: PaymentModelClient = {
        accountName: '',
        bankAccountNumber: '',
        bankAccountType: '',
        branchCode: '',
        methodType: method.paymentMethodTypeCode,
        uuid: method.uuid
      };
      this.paymentsService.initPaymentMethod(blankPaymentMethod);
      // this.router.navigate(['account/settings/billing/add-banking-details']);
    }
  }

  addNewPaymentMethod(): void {
    this.addingPaymentMethod = true;
  }

  toggleIsAddingPaymentMethod(): void {
    this.addingPaymentMethod = !this.addingPaymentMethod;

    this.addingPaymentMethod ? this.editing = "banking" : this.editing = null;
    this.editingChange.emit(this.editing);
    this.resetPaymentMethodForm();
  }

  public onSaveChanges(): void {
    this.valid = false;
    this.errMessage = '';
    const formValue = this.addPaymentMethodForm.getRawValue();
    // if (this.addPaymentMethodForm.valid && this.retailerPaymentMethodTypes.length > 0 && this.paymentTypeOptionSelected(formValue.methodType)) {
    if (this.addPaymentMethodForm.valid) {
      const validateBankAccount = {
        accountNumber: formValue.bankAccountNumber,
        accountType: formValue.bankAccountType,
        branchCode: formValue.bankName,
      };
      this.loading = true;
      lastValueFrom(this.validateBankService.validateAccountDetails(validateBankAccount))
        .then((res: any) => {
          if (res === 'SUCCESS') {
            this.valid = true;
            const model: PaymentModelClient = {
              bankAccountNumber: formValue.bankAccountNumber!,
              bankAccountType: formValue.bankAccountType!,
              branchCode: formValue.bankName!,
              methodType: formValue.methodType!,
              accountName: formValue.accountName!
            };
            this.editingChange.emit(null);
            this.paymentsService.updatePaymentMethod(model);
            this.capitecWarning.emit(false);
            return lastValueFrom(this.paymentsService.createOtpRequest());

          } else {
            this.loading = false;
            switch (res) {
              case 'INVALID_BRANCH_CODE':
                this.errMessage = 'Invalid bank name';
                break;
              case 'INVALID_ACCOUNT_NUMBER':
                this.errMessage = 'Invalid account number';
                break;
              case 'INVALID_ACCOUNT_TYPE':
                this.errMessage = 'Invalid account type';
                break;
              case 'ERROR_AUTH':
                this.errMessage = 'Unexpected error';
                break;
              case 'ERROR_WS':
                this.errMessage = 'Unexpected error';
                break;
              default:
                this.errMessage = 'Unexpected error';
                break;
            }
            this.dialogService.open(ErrorPopupComponent, {
              height: '300px',
              width: '450px',
              data: {
                description: `Could not validate your bank account details: ` + this.errMessage,
              },
            });
          }
          return;
        }, (err: any) => {
          this.handleError(err);
          this.loading = false;
        })
        .then(() => {
          if (this.valid) {
            this.router.navigate(['/settings/billing/verify'])
              .catch((err: string) => {
                console.log(err);
              });
          }
          this.loading = false;
        }, (err: any) => {
          this.handleError(err);
          this.loading = false;
        });
    }
  }

  // public paymentTypeOptionSelected(type: any): boolean {
  //   let returnValue = true;
  //   this.paymentMethodsArray.forEach(savedType => {
  //     if (savedType.paymentMethodTypeCode === type) {
  //       this.addPaymentMethodForm.patchValue({
  //         methodType: 'TRANSACT_PAYBACK',
  //       });
  //       this.dialogService.open(ErrorPopupComponent, {
  //         height: '300px',
  //         width: '350px',
  //         data: {
  //           description: `Payment method type of ${this.paymentsService.getTypeNameFromCode(type)} already exists.`,
  //         },
  //       });
  //       returnValue = false;
  //     }
  //   });
  //   return returnValue;
  // }

  private handleError(err: any): void {
    switch (err.status) {
      case 400:
        if ('ValidationException' === err.headers.get('error_code')) {
          this.errMessage = '' + err.headers.get('error_message');
        }
        this.dialogService.open(ErrorPopupComponent, {
          height: '300px',
          width: '350px',
          data: {
            description: 'unexpected error: ' + this.errMessage,
          },
        });
        break;
      default:
        this.dialogService.open(ErrorPopupComponent, {
          height: '300px',
          width: '350px',
          data: {
            description: 'unexpected error',
          },
        });
    }
  }
}
